import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MoreHorizontal, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { useCurrentWorkspace } from '@/lib/workspace-context';
import {
  useEmailAccounts,
  useEmailAccountActions,
  useMailAccountGroups,
} from '@/hooks/useDispatcher';
import type { EmailAccountItem } from '@/types/api';
import { EmailAccountDetailDrawer } from './EmailAccountDetailDrawer';

const PAGE_SIZE = 50;

type AccountStatus = EmailAccountItem['status'];

function StatusBadge({ status }: { status: AccountStatus }) {
  const tones: Record<AccountStatus, 'green' | 'yellow' | 'red' | 'gray' | 'blue'> = {
    active: 'green',
    warmup: 'yellow',
    suspended: 'red',
    cancelled: 'gray',
    rotation_pause: 'blue',
  };
  return <Badge tone={tones[status] ?? 'gray'}>{status}</Badge>;
}

function ProviderBadge({ provider }: { provider: string }) {
  const tone = provider === 'successai' ? 'purple' : 'blue';
  const label = provider === 'successai' ? 'success.ai' : provider;
  return <Badge tone={tone}>{label}</Badge>;
}

function DailyProgress({ limit, sent }: { limit: number; sent: number }) {
  const pct = limit > 0 ? Math.min(100, Math.round((sent / limit) * 100)) : 0;
  const color = pct >= 90 ? 'bg-red-400' : pct >= 60 ? 'bg-yellow-400' : 'bg-green-400';
  return (
    <div className="flex items-center gap-2 min-w-[80px]">
      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-gray-500 whitespace-nowrap">
        {sent}/{limit}
      </span>
    </div>
  );
}

function WarmupBar({ progress }: { progress: number | null }) {
  if (progress === null) return <span className="text-gray-400 text-xs">—</span>;
  return (
    <div className="flex items-center gap-1.5">
      <div className="w-14 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-blue-400 rounded-full"
          style={{ width: `${Math.min(100, progress)}%` }}
        />
      </div>
      <span className="text-xs text-gray-500">{progress}%</span>
    </div>
  );
}

export default function EmailAccountsPage() {
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id ?? '';

  const [filters, setFilters] = useState({
    provider: '',
    status: '',
    group_id: '',
    available: false,
    q: '',
  });
  const [offset, setOffset] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const { data, isLoading } = useEmailAccounts({
    workspace_id: workspaceId,
    provider: filters.provider || undefined,
    status: filters.status || undefined,
    group_id: filters.group_id || undefined,
    available: filters.available || undefined,
    q: filters.q || undefined,
    limit: PAGE_SIZE,
    offset,
  });

  const { data: groupsData } = useMailAccountGroups(workspaceId);
  const actions = useEmailAccountActions(workspaceId);

  const groups = groupsData?.groups ?? [];
  const groupMap = new Map(groups.map((g) => [g.id, g]));

  const total = data?.total ?? 0;
  const items = data?.items ?? [];
  const pageCount = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  function setFilter<K extends keyof typeof filters>(key: K, value: (typeof filters)[K]) {
    setFilters((f) => ({ ...f, [key]: value }));
    setOffset(0);
  }

  async function handlePause(account: EmailAccountItem) {
    setOpenMenuId(null);
    await actions.pause.mutateAsync(account.id);
    toast.success(`${account.email} mis en pause`);
  }

  async function handleResume(account: EmailAccountItem) {
    setOpenMenuId(null);
    await actions.resume.mutateAsync(account.id);
    toast.success(`${account.email} repris`);
  }

  async function handleDetach(account: EmailAccountItem) {
    setOpenMenuId(null);
    await actions.detach.mutateAsync(account.id);
    toast.success(`${account.email} détaché de la campagne`);
  }

  async function handleSync() {
    const { task_id } = await actions.sync.mutateAsync();
    toast.success(`Sync lancée (${task_id})`);
  }

  const hasFilters = !!(filters.provider || filters.status || filters.group_id || filters.available || filters.q);

  return (
    <div>
      {/* Top bar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <input
          type="text"
          className="input-sm w-44"
          placeholder="Rechercher email…"
          value={filters.q}
          onChange={(e) => setFilter('q', e.target.value)}
        />
        <select
          className="input-sm"
          value={filters.provider}
          onChange={(e) => setFilter('provider', e.target.value)}
        >
          <option value="">Tous les providers</option>
          <option value="successai">success.ai</option>
          <option value="instantly">Instantly</option>
        </select>
        <select
          className="input-sm"
          value={filters.status}
          onChange={(e) => setFilter('status', e.target.value)}
        >
          <option value="">Tous les statuts</option>
          <option value="active">active</option>
          <option value="warmup">warmup</option>
          <option value="suspended">suspended</option>
          <option value="cancelled">cancelled</option>
          <option value="rotation_pause">rotation_pause</option>
        </select>
        <select
          className="input-sm"
          value={filters.group_id}
          onChange={(e) => setFilter('group_id', e.target.value)}
        >
          <option value="">Tous les groupes</option>
          {groups.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer">
          <input
            type="checkbox"
            checked={filters.available}
            onChange={(e) => setFilter('available', e.target.checked)}
          />
          Disponibles
        </label>
        {hasFilters && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setFilters({ provider: '', status: '', group_id: '', available: false, q: '' });
              setOffset(0);
            }}
          >
            Réinitialiser
          </Button>
        )}
        <span className="ml-auto text-sm text-gray-500">
          {total} compte{total !== 1 ? 's' : ''}
        </span>
        <Link to="/settings/email-accounts/groups">
          <Button size="sm" variant="ghost">Groupes</Button>
        </Link>
        <Button size="sm" onClick={handleSync} loading={actions.sync.isPending}>
          <RefreshCw size={13} /> Sync depuis Success.ai
        </Button>
      </div>

      <Card>
        {isLoading ? (
          <div className="p-5">
            <Skeleton className="h-64 w-full" />
          </div>
        ) : items.length === 0 ? (
          <EmptyState title="Aucun compte email trouvé" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-brand-border">
                <tr>
                  <th className="th">Email</th>
                  <th className="th">Domaine</th>
                  <th className="th">Statut</th>
                  <th className="th">Limite / Envoyé</th>
                  <th className="th">Warmup</th>
                  <th className="th">Groupes</th>
                  <th className="th">Campagne</th>
                  <th className="th">Màj</th>
                  <th className="th">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-border">
                {items.map((account) => (
                  <tr
                    key={account.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedId(account.id)}
                  >
                    <td className="td">
                      <div className="flex items-center gap-2">
                        <ProviderBadge provider={account.provider} />
                        <span className="font-mono text-xs text-brand-dark">{account.email}</span>
                      </div>
                    </td>
                    <td className="td text-sm text-gray-500">
                      <span className="hover:text-brand-primary">{account.domain}</span>
                    </td>
                    <td className="td">
                      <StatusBadge status={account.status} />
                    </td>
                    <td className="td">
                      <DailyProgress limit={account.daily_limit} sent={account.current_daily_sent} />
                    </td>
                    <td className="td">
                      <WarmupBar progress={account.warmup_progress} />
                    </td>
                    <td className="td">
                      <div className="flex flex-wrap gap-1">
                        {account.group_ids.map((gid) => {
                          const g = groupMap.get(gid);
                          return g ? (
                            <span
                              key={gid}
                              className="text-xs px-1.5 py-0.5 rounded-full text-white"
                              style={{ backgroundColor: g.color }}
                            >
                              {g.name}
                            </span>
                          ) : null;
                        })}
                      </div>
                    </td>
                    <td className="td">
                      {account.current_assigned_campaign_id ? (
                        <Badge tone="blue">Assigné</Badge>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="td text-xs text-gray-400">
                      {new Date(account.updated_at).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="td" onClick={(e) => e.stopPropagation()}>
                      <div className="relative">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setOpenMenuId(openMenuId === account.id ? null : account.id)}
                        >
                          <MoreHorizontal size={14} />
                        </Button>
                        {openMenuId === account.id && (
                          <div className="absolute right-0 top-8 z-10 bg-white border border-brand-border rounded-lg shadow-lg py-1 w-48">
                            <button
                              className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50"
                              onClick={() => handlePause(account)}
                            >
                              Pause warmup
                            </button>
                            <button
                              className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50"
                              onClick={() => handleResume(account)}
                            >
                              Resume warmup
                            </button>
                            <button
                              className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50"
                              onClick={() => handleDetach(account)}
                            >
                              Détacher de la campagne
                            </button>
                            <button
                              className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50"
                              onClick={() => {
                                setOpenMenuId(null);
                                setSelectedId(account.id);
                              }}
                            >
                              Voir détail
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Pagination */}
      {pageCount > 1 && (
        <div className="flex items-center justify-end gap-2 mt-3">
          <Button
            size="sm"
            variant="ghost"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            ‹ Précédent
          </Button>
          <span className="text-sm text-gray-500">
            {currentPage} / {pageCount}
          </span>
          <Button
            size="sm"
            variant="ghost"
            disabled={offset + PAGE_SIZE >= total}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            Suivant ›
          </Button>
        </div>
      )}

      <EmailAccountDetailDrawer
        accountId={selectedId}
        workspaceId={workspaceId}
        onClose={() => setSelectedId(null)}
      />
    </div>
  );
}
